import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Row, Col, InputGroup, Alert, Container, Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import FormContainer from "../components/FormContainer";
import GoogleIcon from "../components/GoogleIcon";
import Loader from "../components/Loader";
import { useRegisterMutation } from "../slices/usersApiSlice";
import StayTop from "../components/StayTop";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const RegisterScreen = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [phone, setPhone] = useState('');
    const [website, setWebsite] = useState(''); // honeypot - real users never see or fill this
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordMatchError, setPasswordMatchError] = useState('');
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [error, setError] = useState('');
    const [registered, setRegistered] = useState(false);
    const [registeredName, setRegisteredName] = useState('');
    const [registeredEmail, setRegisteredEmail] = useState('');

    const navigate = useNavigate();
    const [register, { isLoading }] = useRegisterMutation();
    const { userInfo } = useSelector((state) => state.auth);
    const { search } = useLocation();
    const sp = new URLSearchParams(search);
    const redirect = sp.get('redirect') || '/';

    // Already logged in
    if (userInfo) {
        return (
            <Container className="py-5">
                <Card className="shadow-sm text-center">
                    <Card.Body className="p-5">
                        <div className="mb-3">
                            <i className="fas fa-check-circle fa-4x text-success"></i>
                        </div>
                        <h2 className="mb-3">You're Already Registered! 🎮</h2>
                        <p className="mb-4 text-muted">
                            You're logged in as <strong>{userInfo.name}</strong>
                        </p>
                        <Button
                            className="btn-orange mb-3"
                            size="lg"
                            onClick={() => navigate('/')}
                            style={{ borderRadius: '50px', padding: '12px 40px', width: '100%', maxWidth: '400px' }}
                        >
                            <i className="fas fa-home"></i> Continue Shopping
                        </Button>
                    </Card.Body>
                </Card>
                <StayTop />
            </Container>
        );
    }

    // Post-registration welcome splash
    if (registered) {
        return (
            <Container className="py-5">
                <Card className="shadow-sm text-center" style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <Card.Body className="p-5">
                        <div className="mb-4">
                            <i className="fas fa-gamepad fa-4x" style={{ color: '#FD7014' }}></i>
                        </div>
                        <h2 className="mb-2" style={{ color: '#3E6D9C' }}>
                            Welcome to RetroDayz, {registeredName}!
                        </h2>
                        <p className="mb-1 text-muted">Your account has been created successfully.</p>
                        <hr />
                        <p className="mb-2">
                            We've sent a verification email to <strong>{registeredEmail}</strong>
                        </p>
                        <p className="mb-2 text-muted" style={{ fontSize: '0.9rem' }}>
                            Please click the link in the email to verify your account before logging in.
                        </p>
                        <p className="mb-4 text-muted" style={{ fontSize: '0.85rem' }}>
                            Don't forget to check your spam or junk folder if you don't see it within a few minutes.
                        </p>
                        {/* Promo mention */}
                        <div
                            className="mb-4 p-3"
                            style={{ backgroundColor: '#FFF8F0', borderRadius: '12px', border: '1px solid #FD7014' }}
                        >
                            <p className="mb-0" style={{ color: '#FD7014', fontWeight: 'bold' }}>
                                🎁 Check your email for your exclusive welcome discount code!
                            </p>
                        </div>
                        <Button
                            className="btn-orange mb-3"
                            size="lg"
                            onClick={() => navigate('/login')}
                            style={{ borderRadius: '50px', padding: '12px 40px', width: '100%', maxWidth: '300px' }}
                        >
                            <i className="fas fa-sign-in-alt"></i> Go to Login
                        </Button>
                        <div>
                            <small className="text-muted">
                                Didn't receive it?{' '}
                                <Link to="/resend-verification">Resend verification email</Link>
                            </small>
                        </div>
                    </Card.Body>
                </Card>
                <StayTop />
            </Container>
        );
    }

    const validateEmailFormat = (email) => {
        const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return regex.test(email);
    };

    const validatePasswordComplexity = (password) => {
        const errors = [];
        if (!/[a-z]/.test(password)) errors.push("At least one lowercase letter");
        if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
        if (!/\d/.test(password)) errors.push("At least one digit");
        if (!/[!@#$%^&*]/.test(password)) errors.push("At least one special character (!@#$%^&*)");
        if (password.length < 8) errors.push("Minimum length of 8 characters");
        return errors;
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordErrors(validatePasswordComplexity(newPassword));
        setError('');
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        setPasswordMatchError(password !== newConfirmPassword ? "Passwords do not match." : "");
        setError('');
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setError('');

        if (!agreeTerms) {
            setError("You must agree to the terms and conditions");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        const errors = validatePasswordComplexity(password);
        if (errors.length > 0) {
            setError("Please fix the password requirements listed below");
            return;
        }

        try {
            await register({
                name,
                email,
                phone,
                password,
                agreeTerms,
                marketingConsent,
                website,
                date: new Date()
            }).unwrap();

            setRegisteredName(name);
            setRegisteredEmail(email);
            setRegistered(true);
        } catch (err) {
            setError(err?.data?.message || err.error || 'Registration failed. Please try again.');
        }
    };

    const handleGoogleRegister = () => {
        const baseUrl = process.env.NODE_ENV === 'development'
            ? 'http://localhost:5001'
            : 'https://retrodayz.co.uk';
        window.location.href = `${baseUrl}/api/users/auth/google`;
    };

    return (
        <div style={{ paddingTop: '30px' }}>
            <FormContainer>
            <h1>Sign Up</h1>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            {/* Google Sign Up */}
            <Button
                onClick={handleGoogleRegister}
                style={{
                    width: '100%',
                    borderRadius: '50px',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    color: '#444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '10px',
                    fontWeight: '500',
                    marginBottom: '16px',
                }}
            >
                <GoogleIcon />
                Continue with Google
            </Button>

            <div className="text-center text-muted mb-3">or register with email</div>

            <Form onSubmit={submitHandler}>
                {/* Honeypot field - hidden from sighted/keyboard users, bots tend to fill every field */}
                <Form.Group
                    controlId="website"
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                    aria-hidden="true"
                >
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                        type="text"
                        tabIndex="-1"
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                    />
                </Form.Group>

                <Form.Group controlId="name" className="my-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter name"
                        value={name}
                        onChange={(e) => { setName(e.target.value); setError(''); }}
                    />
                </Form.Group>

                <Form.Group controlId="email" className="my-3">
                    <Form.Label>Email Address (verification required)</Form.Label>
                    <Form.Control
                        type="email"
                        placeholder="Enter email"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setIsEmailValid(validateEmailFormat(e.target.value));
                            setError('');
                        }}
                    />
                    {!isEmailValid && email && (
                        <div className="text-danger">Please enter a valid email address.</div>
                    )}
                </Form.Group>

                <Form.Group controlId="phone" className="my-3">
                    <Form.Label>Phone Number <span className="text-muted">(optional)</span></Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter phone number"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); setError(''); }}
                    />
                </Form.Group>

                <Form.Group controlId="password" className="my-3">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter password"
                            autoComplete="off"
                            value={password}
                            onChange={handlePasswordChange}
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                    </InputGroup>
                    {passwordErrors.length > 0 && password && (
                        <ul className="text-danger mt-2">
                            <strong>Password requirements:</strong>
                            {passwordErrors.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    )}
                </Form.Group>

                <Form.Group controlId="confirmPassword" className="my-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                    </InputGroup>
                    {passwordMatchError && confirmPassword && (
                        <div className="text-danger mt-2">{passwordMatchError}</div>
                    )}
                </Form.Group>

                <Form.Group controlId="terms" className="my-3">
                    <Form.Check
                        type="checkbox"
                        label={
                            <>
                                I agree to the{' '}
                                <Link to="/terms" target="_blank" rel="noopener noreferrer">
                                    terms and conditions
                                </Link>
                            </>
                        }
                        checked={agreeTerms}
                        onChange={(e) => { setAgreeTerms(e.target.checked); setError(''); }}
                    />
                </Form.Group>

                <Form.Group controlId="marketingConsent" className="my-3">
                    <Form.Check
                        type="checkbox"
                        label={
                            <span>
                                📧 I want to receive exclusive offers, new product alerts, and special promotions from RetroDayz{' '}
                                <span className="text-muted">(optional)</span>
                            </span>
                        }
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                        You can unsubscribe at any time. See our{' '}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</Link>{' '}
                        for details.
                    </Form.Text>
                </Form.Group>

                {isLoading && <Loader />}

                <Button
                    type="submit"
                    variant="primary"
                    className="mt-3"
                    style={{ width: '100%', borderRadius: '50px' }}
                    disabled={
                        isLoading ||
                        passwordErrors.length > 0 ||
                        !!passwordMatchError ||
                        !isEmailValid ||
                        !email ||
                        !password ||
                        !confirmPassword ||
                        !agreeTerms
                    }
                >
                    Create Account
                </Button>
            </Form>

            <Row className="py-3">
                <Col>
                    Already registered?{' '}
                    <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
                </Col>
            </Row>
            <StayTop />
        </FormContainer>
        </div>
    );
};

export default RegisterScreen;